import { h } from 'vue';

import {
  createVuetify,
  type IconProps,
  type ThemeDefinition,
  type VuetifyOptions,
} from 'vuetify';
import {
  VAutocomplete,
  VBtn,
  VChip,
  VLigatureIcon,
  VList,
  VTabs,
} from 'vuetify/components';
import { aliases } from 'vuetify/iconsets/md';
import {
  de,
  en,
  es,
  fr,
  it,
  pt,
} from 'vuetify/locale';

import IconAmex from './components/icon/IconAmex.vue';
import IconAppAcademics from './components/icon/IconAppAcademics.vue';
import IconAppAMS from './components/icon/IconAppAMS.vue';
import IconAppCamps from './components/icon/IconAppCamps.vue';
import IconAppCollectives from './components/icon/IconAppCollectives.vue';
import IconAppCommunities from './components/icon/IconAppCommunities.vue';
import IconAppCompliance from './components/icon/IconAppCompliance.vue';
import IconAppGm from './components/icon/IconAppGM.vue';
import IconAppHub from './components/icon/IconAppHub.vue';
import IconAppInfluencer from './components/icon/IconAppInfluencer.vue';
import IconAppNutrition from './components/icon/IconAppNutrition.vue';
import IconAppPathways from './components/icon/IconAppPathways.vue';
import IconAppPulse from './components/icon/IconAppPulse.vue';
import IconAppRCommunication from './components/icon/IconAppRCommunication.vue';
import IconAppRecruiting from './components/icon/IconAppRecruiting.vue';
import IconAppStrength from './components/icon/IconAppStrength.vue';
import IconAppSwitcher from './components/icon/IconAppSwitcher.vue';
import IconAppTeamworks from './components/icon/IconAppTeamworks.vue';
import IconAppTrac from './components/icon/IconAppTrac.vue';
import IconAppWallet from './components/icon/IconAppWallet.vue';
import IconBankTransfer from './components/icon/IconBankTransfer.vue';
import IconCoaching from './components/icon/IconCoaching.vue';
import IconChatSupport from './components/icon/IconChatSupport.vue';
import IconConversation from './components/icon/IconConversation.vue';
import IconDefaultAvatar from './components/icon/IconDefaultAvatar.vue';
import IconDigitalDisplays from './components/icon/IconDigitalDisplays.vue';
import IconDinersClub from './components/icon/IconDinersClub.vue';
import IconDiscover from './components/icon/IconDiscover.vue';
import IconFacebook from './components/icon/IconFacebook.vue';
import IconInflcrHex from './components/icon/IconInflcrHex.vue';
import IconInfluential from './components/icon/IconInfluential.vue';
import IconInstagram from './components/icon/IconInstagram.vue';
import IconJCB from './components/icon/IconJCB.vue';
import IconLinkedIn from './components/icon/IconLinkedIn.vue';
import IconMastercard from './components/icon/IconMastercard.vue';
import IconMessagesDollar from './components/icon/IconMessagesDollar.vue';
import IconNcaa from './components/icon/IconNcaa.vue';
import IconNotemeal from './components/icon/IconNotemeal.vue';
import IconOn3 from './components/icon/IconOn3.vue';
import IconScouting from './components/icon/IconScouting.vue';
import IconSparkle from './components/icon/IconSparkle.vue';
import IconTeamworks from './components/icon/IconTeamworks.vue';
import IconTikTok from './components/icon/IconTikTok.vue';
import IconVectorDown from './components/icon/IconVectorDown.vue';
import IconVectorUp from './components/icon/IconVectorUp.vue';
import IconVisa from './components/icon/IconVisa.vue';
import IconWhistle from './components/icon/IconWhistle.vue';
import IconX from './components/icon/IconX.vue';

import {
  darkDefaultHex,
  lightDefaultHex,
} from './utils/colorContrast';

const TwLight: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#fcfcfc',
    surface: '#ffffff',
    primary: '#EAA934',
    'primary-button': '#000000',
    error: '#d83d32',
    info: '#045dff',
    success: '#0a893c',
    'success-25': '#e7f3ec',
    warning: '#f79009',
    'border-light': '#eaecf0',
    'border-dark': '#414445',
    anchor: '#000000',
    purple: '#7f56d9',
    'input-border-color': '#E7E8E8',
    'neutral-25': '#fcfcfc',
    'neutral-50': '#f8fafb',
    'neutral-100': '#f2f4f7',
    'neutral-200': '#eaecf0',
    'neutral-300': '#d0d5dd',
    'neutral-400': '#98a2b3',
    'neutral-500': '#667085',
    'neutral-600': '#475467',
    'neutral-700': '#344054',
    'neutral-800': '#1d2939',
    'neutral-900': '#101828',
    'info-25': '#e6efff',
    'brands-facebook': '#0866ff',
    'brands-influential': '#1866fe',
    'brands-instagram': '#fc3857',
    'brands-linkedin': '#0966c2',
    'brands-on3': '#f93b1d',
    'brands-tiktok': '#000000',
    'brands-x': '#000000',
    'brands-amex': '#2671b9',
    'brands-diners': '#006699',
    'brands-discover': '#e55C20',
    'brands-jcb': '#0C4496',
    'brands-mastercard': '#eb001b',
    'brands-visa': '#1434cb',
    'axle-navbar-top': '#000000',
    'axle-navbar-bottom': '#EAA934',
    'school-primary': darkDefaultHex,
    'school-background': lightDefaultHex,
    'school-header-background': darkDefaultHex,
    'fee-type-hourly': '#E8F0FF',
    'fee-type-fixed-cost': '#F6FBED',
    'fee-type-percent-of-deals': '#F3EFFA',
    'fee-type-negotiated': '#FFE8DB',
    'products-finance-primary': '#EAA934',
    'products-finance-secondary': '#FBDAA1',
    'products-finance-tertiary': '#FFEECE',
    'products-operations-primary': '#1A6AFF',
    'products-operations-secondary': '#BAD2FF',
    'products-operations-tertiary': '#E8F0FF',
    'products-branding-primary': '#2CC3F2',
    'products-branding-secondary': '#C0EDFB',
    'products-branding-tertiary': '#EAF9FE',
    'products-performance-primary': '#0A8560',
    'products-performance-secondary': '#B6DACF',
    'products-performance-tertiary': '#E7F3EF',
    'products-recruiting-primary': '#A0D346',
    'products-recruiting-secondary': '#E3F2C8',
    'products-recruiting-tertiary': '#F6FBED',
    'products-development-primary': '#8362CB',
    'products-development-secondary': '#DAD0EF',
    'products-development-tertiary': '#F3EFFA',
    'feedback-error-700': '#D83D32',
    'feedback-error-200': '#EFB1AD',
    'feedback-error-25': '#FBECEB',
    'accent-orange-500': '#EF6820',
    'accent-orange-200': '#F9DBAF',
    'accent-orange-25': '#FEF4ED',
    'accent-yellow-400': '#FAC515',
    'accent-yellow-200': '#FEEE95',
    'accent-yellow-25': '#FEFDF0',
    'accent-pink-900': '#841651',
    'accent-pink-700': '#F9B4C7',
    'accent-pink-500': '#EE46BC',
    'accent-pink-400': '#F670C7',
    'accent-pink-200': '#FCCEEE',
    'accent-pink-25': '#FEF6FB',
  },
};

const TwDark: ThemeDefinition = {
  dark: true,
  colors: {
    background: '#000000',
    surface: '#121516',
    primary: '#EAA934',
    'primary-button': '#1A6AFF',
    error: '#d83d32',
    info: '#045dff',
    success: '#0a893c',
    warning: '#f79009',
    'border-light': '#d0d5dd',
    'border-dark': '#414445',
    anchor: '#000000',
    purple: '#7f56d9',
    'neutral-25': '#e7e8e8',
    'neutral-50': '#d0d0d0',
    'neutral-100': '#b8b9b9',
    'neutral-200': '#a0a1a2',
    'neutral-300': '#898a8b',
    'neutral-400': '#717373',
    'neutral-500': '#595b5c',
    'neutral-600': '#414445',
    'neutral-700': '#2a2c2d',
    'neutral-800': '#121516',
    'neutral-900': '#0b0d0d',
    'brands-facebook': '#0866ff',
    'brands-influential': '#1866fe',
    'brands-instagram': '#fc3857',
    'brands-linkedin': '#0966c2',
    'brands-on3': '#f93b1d',
    'brands-tiktok': '#000000',
    'brands-x': '#000000',
    'brands-amex': '#2671b9',
    'brands-diners': '#006699',
    'brands-discover': '#e55C20',
    'brands-jcb': '#0C4496',
    'brands-mastercard': '#eb001b',
    'brands-visa': '#1434cb',
    'axle-navbar-top': '#000000',
    'axle-navbar-bottom': '#2CC3F2',
    'school-primary': darkDefaultHex,
    'school-background': lightDefaultHex,
    'school-header-background': darkDefaultHex,
    'fee-type-hourly': '#E8F0FF',
    'fee-type-fixed-cost': '#F6FBED',
    'fee-type-percent-of-deals': '#F3EFFA',
    'fee-type-negotiated': '#FFE8DB',
  },
};

export const twDefaults = {
  VChipMini: {
    border: true,
    class: ['v-chip-mini', 'text-caption', 'px-1'],
    closeIcon: 'close',
    size: 'x-small',
    label: true,
    style: ['--v-activated-opacity: 0.03', 'font-weight: 400'],
  },
  VBtnPrimary: {
    color: 'primary-button',
    elevation: 1,
    variant: 'flat',
  } as typeof VBtn.props,
  VBtnSecondary: {
    border: 'sm solid border-light opacity-100',
    class: 'text-decoration-none bg-background',
    elevation: 1,
    variant: 'outlined',
  } as typeof VBtn.props,
  VBtnDanger: {
    color: 'error',
    elevation: 1,
    variant: 'flat',
  } as typeof VBtn.props,
  VBtnText: {
    size: 'small',
    variant: 'text',
  } as typeof VBtn.props,
};

export const defaultVuetifyConfig: VuetifyOptions = {
  display: {
    mobileBreakpoint: 'md',
  },
  theme: {
    defaultTheme: 'TwLight',
    themes: {
      TwLight,
      TwDark,
    },
  },
  icons: {
    defaultSet: 'md',
    aliases: {
      ...aliases,
      'twi-app-gm': IconAppGm,
      'twi-inflcr-hex': IconInflcrHex,
      'twi-notemeal': IconNotemeal,
      'twi-teamworks': IconTeamworks,
      'twi-whistle': IconWhistle,
      'twi-conversation': IconConversation,
      'twi-avatar': IconDefaultAvatar,
      warning: 'warning',
      'twi-chat-support': IconChatSupport,
      'twi-facebook': IconFacebook,
      'twi-influential': IconInfluential,
      'twi-instagram': IconInstagram,
      'twi-linkedin': IconLinkedIn,
      'twi-messages-dollar': IconMessagesDollar,
      'twi-on3': IconOn3,
      'twi-tiktok': IconTikTok,
      'twi-x': IconX,
      'twi-vector-up': IconVectorUp,
      'twi-vector-down': IconVectorDown,
      'twi-amex': IconAmex,
      'twi-diners': IconDinersClub,
      'twi-discover': IconDiscover,
      'twi-jcb': IconJCB,
      'twi-mastercard': IconMastercard,
      'twi-visa': IconVisa,
      'twi-bank-transfer': IconBankTransfer,
      'twi-ncaa': IconNcaa,
      'twi-app-academics': IconAppAcademics,
      'twi-app-ams': IconAppAMS,
      'twi-app-camps': IconAppCamps,
      'twi-app-coaching': IconCoaching,
      'twi-app-collectives': IconAppCollectives,
      'twi-app-communities': IconAppCommunities,
      'twi-app-compliance': IconAppCompliance,
      'twi-app-hub': IconAppHub,
      'twi-app-influencer': IconAppInfluencer,
      'twi-app-nutrition': IconAppNutrition,
      'twi-app-pathways': IconAppPathways,
      'twi-app-pulse': IconAppPulse,
      'twi-app-r-communication': IconAppRCommunication,
      'twi-app-recruiting': IconAppRecruiting,
      'twi-app-scouting': IconScouting,
      'twi-app-strength': IconAppStrength,
      'twi-app-switcher': IconAppSwitcher,
      'twi-app-teamworks': IconAppTeamworks,
      'twi-app-trac': IconAppTrac,
      'twi-app-wallet': IconAppWallet,
      'twi-sparkle': IconSparkle,
      'twi-app-digital-displays': IconDigitalDisplays,
    },
    sets: {
      md: {
        component: (props: IconProps) =>
          h(VLigatureIcon, {
            ...props,
            class: 'material-icons-round',
          }),
      },
    },
  },
  aliases: {
    VAutocompleteMulti: VAutocomplete,
    VBtnPrimary: VBtn,
    VBtnSecondary: VBtn,
    VBtnSchool: VBtn,
    VBtnDanger: VBtn,
    VBtnText: VBtn,
    VBtnTileSecondary: VBtn,
    VBtnIcon: VBtn,
    VBtnEditorTool: VBtn,
    VChipTag: VChip,
    VChipMini: VChip,
    VChipCategory: VChip,
    VMenuList: VList,
    VAthleteList: VList,
    VTabsNavigation: VTabs,
  },
  defaults: {
    VAutocomplete: {
      density: 'comfortable',
      variant: 'outlined',
      menuIcon: 'expand_more',
      persistentClear: true,
      VMenu: {
        offset: 0,
      },
    },
    VAutocompleteMulti: {
      class: 'v-autocomplete-multi border rounded',
      density: 'comfortable',
      variant: 'outlined',
      menuIcon: 'expand_more',
      persistentClear: true,
      chips: true,
      closableChips: true,
      multiple: true,
      VChip: {
        ...twDefaults.VChipMini,
        class: [...twDefaults.VChipMini.class, 'pr-2'],
        style: [...twDefaults.VChipMini.style, '--v-chip-height: 24px'],
      },
      VMenu: {
        offset: 0,
      },
    },
    VAlert: {
      variant: 'tonal',
    },
    VBtn: {
      class: 'text-decoration-none',
    },
    VBtnPrimary: twDefaults.VBtnPrimary,
    VBtnSchool: {
      ...twDefaults.VBtnPrimary,
      color: 'school-primary',
    },
    VBtnSecondary: twDefaults.VBtnSecondary,
    VBtnDanger: twDefaults.VBtnDanger,
    VBtnText: twDefaults.VBtnText,
    VBtnTileSecondary: {
      border: 'sm solid border-light opacity-100',
      class: 'px-0',
      elevation: 1,
      variant: 'outlined',
      style: { minWidth: 'var(--v-btn-height)' },
    },
    VBtnIcon: {
      class: 'px-0',
      style: { minWidth: 'var(--v-btn-height)' },
      variant: 'text',
      rounded: true,
    },
    VBtnEditorTool: {
      class: 'v-btn-editor-tool pa-0',
      ripple: false,
      variant: 'text',
      style: ['--v-btn-height: 28px', { minWidth: 'var(--v-btn-height)' }],
      VIcon: {
        size: 16,
      },
    },
    VChip: {
      class: 'flex-0-0',
      style: ['padding: 0 8px'],
      variant: 'flat',
    },
    VChipMini: twDefaults.VChipMini,
    VChipTag: {
      color: 'white',
      elevation: 1,
      label: true,
      ripple: false,
      variant: 'elevated',
    },
    VChipCategory: {
      closeIcon: 'close',
      density: 'compact',
      variant: 'outlined',
      class: 'px-2',
      style: { borderColor: 'rgb(var(--v-theme-border-light))' },
    },
    VCombobox: {
      density: 'comfortable',
      variant: 'outlined',
      menuIcon: 'expand_more',
      persistentClear: true,
      chips: true,
      closableChips: true,
      multiple: true,
      VChip: {
        ...twDefaults.VChipMini,
        class: [...twDefaults.VChipMini.class, 'pr-2'],
        style: [...twDefaults.VChipMini.style, '--v-chip-height: 24px'],
      },
      VMenu: {
        offset: 0,
      },
    },
    VDialog: {
      origin: 'center center',
      transition: 'fade-transition',
      zIndex: 1000000,
    },
    VListItemSubtitle: {
      class: 'text-caption font-weight-medium',
    },
    VMenuList: {
      nav: true,
      minWidth: 240,
      bgColor: 'background',
      density: 'compact',
    },
    VAthleteList: {
      bgColor: 'background',
      lines: false,
      VListItemSpacer: {
        class: 'mr-2',
      },
      VListItem: {
        class: 'py-2',
      },
      VListItemTitle: {
        class: 'text-body-2 font-weight-semibold',
      },
      VListItemSubtitle: {
        class: 'text-body-2 font-weight-regular',
      },
    },
    VDataTableServer: {
      class: 'border rounded bg-background',
      mustSort: true,
      sortAscIcon: 'arrow_drop_up',
      sortDescIcon: 'arrow_drop_down',
    },
    VIcon: {
      size: '20',
    },
    VMenu: {
      offset: 4,
      zIndex: 1000001,
    },
    VPagination: {
      density: 'compact',
      nextIcon: 'arrow_forward',
      prevIcon: 'arrow_back',
      totalVisible: 5,
    },
    VProgressCircular: {
      color: 'primary',
      indeterminate: true,
    },
    VProgressLinear: {
      color: 'primary',
      indeterminate: true,
      rounded: true,
    },
    VSelect: {
      density: 'comfortable',
      variant: 'outlined',
      menuIcon: 'expand_more',
      persistentClear: true,
      VMenu: {
        offset: 0,
      },
    },
    VStepperItem: {
      color: 'neutral-500',
      completeIcon: 'check_circle',
      icon: 'radio_button_unchecked',
      VAvatar: {
        variant: 'outlined',
        color: 'neutral-500',
        size: '28px',
      },
    },
    VTextarea: {
      color: 'border-light',
      density: 'comfortable',
      variant: 'outlined',
    },
    VSheet: {
      color: 'surface',
    },
    VSwitch: {
      inset: true,
    },
    VTabs: {
      bgColor: 'neutral-200',
      class: 'border rounded',
      height: 42,
      VTab: {
        hideSlider: true,
        selectedClass: 'bg-neutral-50 elevation-1',
      },
    },
    VTabsNavigation: {
      height: 42,
    },
    VTextField: {
      color: 'border-light',
      density: 'comfortable',
      variant: 'outlined',
      hideSpinButtons: true,
    },
    VTimeline: {
      density: 'compact',
      lineThickness: '1',
      side: 'end',
      truncateLine: 'both',
      VTimelineItem: {
        dotColor: 'background',
        iconColor: 'info',
      },
    },
    VTooltip: {
      contentClass: 'elevation-2',
      maxWidth: 250,
    },
  },
  locale: {
    locale: 'en-US',
    fallback: 'en-US',
    messages: {
      'en-US': en,
      'fr-FR': fr,
      'pt-BR': pt,
      'it-IT': it,
      'es-ES': es,
      'de-DE': de,
    },
  },
};

// Set Vuetify after extending the theme
let vuetifyInstance: ReturnType<typeof createVuetify> | null = null;

export const getVuetify = (options: VuetifyOptions | undefined) => {
  if (!vuetifyInstance) {
    vuetifyInstance = createVuetify({
      blueprint: defaultVuetifyConfig,
      ...options,
    });
  }
  return vuetifyInstance;
};
